import I18n from 'i18n-js';
import React, { useContext, useEffect } from 'react';

import { gql } from '@reverbdotcom/commons/src/gql';
import { RCCheckbox, RCIcon, RCTag, RCText } from '@reverbdotcom/cadence/components';
import { TagIcon, InfoIcon } from '@phosphor-icons/react';
import { FormCards } from '@reverbdotcom/commons/src/components/FormCards';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';

import { SellFormContext } from '../../SellFormContext';
import { Field, ActionType } from '../../sellFormReducerTypes';
import { withinAccessPointLimits, accessPointEligibleCategory } from '../../../../discovery/shipping_labels/shipping_label_package';

const SELLER_ACCESS_POINTS_ARTICLE_ID = '53752523634971';

export default function CarrierCalcPickupStore() {
  const { listingEntry, dispatch, sellFormViewData } = useContext(SellFormContext);
  const packageEntry = listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const allowPickupStore = !!packageEntry?.shipsToAccessPoints;
  const eligible = !!sellFormViewData?.accessPointShippingOptInEligible &&
    withinAccessPointLimits(packageEntry) &&
    accessPointEligibleCategory(listingEntry[Field.ROOT_CATEGORY_UUID]);
  const accessPointsHcUrl = useHelpCenterArticleUrl(SELLER_ACCESS_POINTS_ARTICLE_ID);

  // Clear a stale opt-in if the seller or the listing is no longer eligible (e.g. the
  // seller edited the dimensions, recategorized the listing, or lost their access point
  // eligibility after opting in), so we never persist a selection the backend will reject
  // and never summarize the listing as shipping to pickup stores when it won't.
  useEffect(() => {
    if (!eligible && allowPickupStore) {
      dispatch({
        type: ActionType.UPDATE_FIELD,
        fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
        value: { ...packageEntry, shipsToAccessPoints: false },
      });
    }
  }, [eligible, allowPickupStore, packageEntry, dispatch]);

  if (!eligible) { return null; }

  return (
    <FormCards.Card>
      <FormCards.FormSection
        title={I18n.t('discovery.sellForm.shippingSection.carrierCalcPickupStore.header')}
        fullWidthForm
      >
        <RCCheckbox
          id="carrier-calc-pickup-store"
          name="carrier-calc-pickup-store"
          checked={allowPickupStore}
          label={(
            <>
              <span className="mr-2">
                {I18n.t('discovery.sellForm.shippingSection.carrierCalcPickupStore.checkboxLabel')}
              </span>
              <RCTag variant="highlight">
                {I18n.t('commons.formGroup.recommended')}
              </RCTag>
            </>
          )}
          onChange={() => {
            dispatch({
              type: ActionType.UPDATE_FIELD,
              fieldKey: Field.CARRIER_CALCULATED_PACKAGE,
              value: { ...packageEntry, ...{ shipsToAccessPoints: !allowPickupStore } },
            });
          }}
        />

        <div className="d-flex fx-align-center bg-color-on-primary padding-5 bd-radius-md mt-3">
          <div className="mr-2 fg-color-text-secondary">
            <RCIcon size="6" icon={<TagIcon />} />
          </div>
          <div className="mb-0 size-90">
            <span className="weight-bold">
              {I18n.t('discovery.sellForm.shippingSection.carrierCalcPickupStore.messageHighlight')}
            </span>
            {' '}
            {I18n.t('discovery.sellForm.shippingSection.carrierCalcPickupStore.message')}
            {' '}
            <CoreLink
              to={accessPointsHcUrl}
              target="_blank"
              className="fg-color-text-primary td-underline"
            >
              {I18n.t('discovery.sellForm.shippingSection.carrierCalcPickupStore.learnMoreLinkText')}
            </CoreLink>
          </div>
        </div>

        {allowPickupStore && (
          <div className="mt-3">
            <RCText.Fine htmlTag="div" color="secondary">
              <RCIcon icon={<InfoIcon />} size="inline" spacing="right" />
              {I18n.t('discovery.sellForm.shippingSection.carrierCalcPickupStore.cancelWarning')}
            </RCText.Fine>
          </div>
        )}
      </FormCards.FormSection>
    </FormCards.Card>
  );
}

export const SellFormAccessPointShippingDataFragment = gql(`
  fragment SellFormAccessPointShippingData on rql_Me {
    _id
    accessPointShippingOptInEligible
  }
`);
